// =============================================================================
// GameApp — top-level component for the actual game (vs. the engine demo).
//
// Renders one of: BootScene, AuthScene, OnboardingScene, WorldScreen, BattleScreen
// based on `stage`. Owns the singleton GlobalRoom (chat + notifications).
// =============================================================================

import { useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import type { Room } from "colyseus.js";
import { GameContext, type GameContextValue, type GameSession, type GameStage } from "@game/game-context";
import { GameClient } from "@game/services";
import { BootScene } from "@game/scenes/BootScene";
import { AuthScene } from "@game/scenes/AuthScene";
import { OnboardingScene } from "@game/scenes/OnboardingScene";
import { WorldScreen } from "@game/screens/WorldScreen";
import { BattleScreen } from "@game/screens/BattleScreen";
import type { GlobalState, Me, OwnedDigimon, Wallet } from "@game/types";

interface Props {
  /** Backend HTTP base URL. */
  httpUrl?: string;
  /** Backend WebSocket URL. */
  wsUrl?: string;
}

// HTTP defaults to the page origin. In dev (`bun dev`), Vite's proxy forwards
// backend routes to digimon.digital (avoids CORS). In prod, origin is digimon.digital.
const DEFAULT_HTTP =
  (import.meta as { env?: Record<string, string> }).env?.VITE_HTTP_URL
  ?? (typeof window !== "undefined" ? window.location.origin : "https://digimon.digital");
// WebSocket: always direct to digimon.digital. Browsers don't enforce CORS on
// WS handshakes, and Vite can't reliably proxy WS upgrades for Colyseus.
const DEFAULT_WS =
  (import.meta as { env?: Record<string, string> }).env?.VITE_WS_URL ?? "wss://digimon.digital";

export function GameApp({ httpUrl = DEFAULT_HTTP, wsUrl = DEFAULT_WS }: Props) {
  // GameClient is a singleton per app instance.
  const client = useMemo(
    () =>
      new GameClient({
        httpUrl,
        wsUrl,
        onAuthFailed: () => {
          // Force re-login from anywhere
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          setStage({ kind: "login" });
        },
      }),
    [httpUrl, wsUrl],
  );

  const [stage, setStage] = useState<GameStage>({ kind: "boot" });
  const [globalRoom, setGlobalRoom] = useState<Room<GlobalState> | null>(null);

  // Connect to the `global` room as soon as we have an auth token.
  useEffect(() => {
    if (!client.auth.isAuthenticated()) {
      setGlobalRoom((prev) => {
        prev?.leave();
        return null;
      });
      return;
    }
    if (globalRoom) return;
    let cancelled = false;
    void (async () => {
      try {
        const tok = client.auth.current()?.token;
        if (!tok) return;
        const room = await client.colyseus.joinOrCreate<GlobalState>(
          "global",
          { token: tok },
          { onLost: () => { if (!cancelled) setGlobalRoom(null); } },
        );
        if (cancelled) {
          room.leave();
          return;
        }
        setGlobalRoom(room);
      } catch (err) {
        // ignore — chat will retry on next auth event
        void err;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, globalRoom, stage.kind]);

  // Logout teardown
  useEffect(() => {
    const unsub = client.auth.subscribe((auth) => {
      if (!auth) {
        globalRoom?.leave();
        setGlobalRoom(null);
      }
    });
    return unsub;
  }, [client, globalRoom]);

  const refreshSession = useCallback(async (): Promise<void> => {
    if (stage.kind !== "world" && stage.kind !== "battle") return;
    try {
      const [me, digimons, wallet] = await Promise.all([
        client.api.get<Me>("/me"),
        client.api.get<{ digimons: OwnedDigimon[] }>("/digimons"),
        client.api.get<Wallet>("/wallet"),
      ]);
      const newSession: GameSession = {
        me,
        position: stage.session.position,
        digimons: digimons.digimons,
        wallet,
      };
      setStage({ ...stage, session: newSession } as GameStage);
    } catch (err) {
      void err;
    }
  }, [client, stage]);

  const ctx: GameContextValue = useMemo(
    () => ({ client, stage, setStage, refreshSession }),
    [client, stage, refreshSession],
  );

  return (
    <GameContext.Provider value={ctx}>
      <Router stage={stage} globalRoom={globalRoom} />
    </GameContext.Provider>
  );
}

function Router({ stage, globalRoom }: { stage: GameStage; globalRoom: Room<GlobalState> | null }): ReactNode {
  switch (stage.kind) {
    case "boot":
      return <BootScene />;
    case "login":
    case "register":
      return <AuthScene />;
    case "onboarding":
      return <OnboardingScene />;
    case "verify-email":
      return <BootScene />; // TODO: dedicated verify screen
    case "world":
      return <WorldScreen session={stage.session} globalRoom={globalRoom} />;
    case "battle":
      return <BattleScreen roomId={stage.roomId} mode={stage.mode} />;
    case "error":
      return <ErrorScreen message={stage.message} retryable={stage.retryable} />;
  }
}

function ErrorScreen({ message, retryable }: { message: string; retryable: boolean }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#0b0d12",
        color: "#e6e8ee",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 12,
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
      }}
    >
      <div style={{ color: "#ef4444", fontSize: 18 }}>Something went wrong</div>
      <div style={{ opacity: 0.7, fontSize: 13, maxWidth: 480, textAlign: "center" }}>{message}</div>
      {retryable && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            padding: "6px 14px",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}
